// -----------------------------------------------------------
// PatNonCausalPanel.jsx
// Interactive non-causal reformulation gate for PAT-GPT
// -----------------------------------------------------------

import React, { useState } from "react";

function normaliseNonCausalDraft(value) {
  if (!value || typeof value !== "object") {
    return {
      pattern: "direct",
      X: "",
      Y: "",
      M: "",
      Z: "",
      customReformulation: "",
      includeOriginalContext: false,
      confirmed: false,
      showPatternExamples: false,
    };
  }

  return {
    pattern: value.pattern || "direct",
    X: value.X || "",
    Y: value.Y || "",
    M: value.M || "",
    Z: value.Z || "",
    customReformulation: value.customReformulation || "",
    includeOriginalContext: value.includeOriginalContext !== false,
    confirmed: value.confirmed === true,
    showPatternExamples: value.showPatternExamples === true,
  };
}

function buildCausalReformulationFromDraft(draft) {
  const d = normaliseNonCausalDraft(draft);

  const X = d.X.trim();
  const Y = d.Y.trim();
  const M = d.M.trim();
  const Z = d.Z.trim();

  if (!X || !Y) return "";

  if (d.pattern === "increase_decrease") {
    return `Does ${X} increase or decrease ${Y}?`;
  }

  if (d.pattern === "counterfactual") {
    return `Would ${Y} change if ${X} were altered?`;
  }

  if (d.pattern === "mediated") {
    if (M) {
      return `Does ${X} affect ${Y}, and is that relationship mediated by ${M}?`;
    }

    return `Does ${X} affect ${Y}, and is that relationship mediated by a specified mediator?`;
  }

  if (d.pattern === "confounded") {
    if (Z) {
      return `Does ${X} affect ${Y} after adjusting for ${Z}?`;
    }

    return `Does ${X} affect ${Y} after adjusting for a specified confounder?`;
  }

  return `Does ${X} cause ${Y}?`;
}

function nonCausalPatternLabel(pattern) {
  switch (pattern) {
    case "increase_decrease":
      return "Does X increase or decrease Y?";
    case "counterfactual":
      return "Would Y change if X were altered?";
    case "mediated":
      return "Is the effect of X on Y mediated by M?";
    case "confounded":
      return "Is the relation between X and Y adjusted for Z?";
    case "direct":
    default:
      return "Does X cause Y?";
  }
}

function classifyNonCausalMode(input) {
  const text = String(input || "").toLowerCase();

  if (
    /\b(indicate|signal|suggest|marker|sign of|evidence of|diagnostic|screen for)\b/.test(
      text,
    )
  ) {
    return {
      label: "Indicator or diagnostic wording",
      explanation:
        "This input asks whether one observation is evidence, a sign, or an indicator of another condition. That is not yet a causal model because indication runs through evidential interpretation rather than intervention.",
      repair:
        "To proceed causally, nominate the condition, exposure, failure state, or behaviour that may change an outcome. For example, reformulate from “Does X indicate Y?” toward “Does Y increase X?” or “Does Z increase X?”, depending on the intended causal direction.",
    };
  }

  if (
    /\b(associated with|correlated with|related to|relationship between|linked to|connection between)\b/.test(
      text,
    )
  ) {
    return {
      label: "Association wording",
      explanation:
        "This input describes a relationship or co-occurrence, but association alone does not authorise a directed causal graph.",
      repair:
        "To proceed causally, specify which variable is being treated as the potential cause, exposure, condition, or intervention (X), and which variable is the outcome that may change (Y).",
    };
  }

  if (
    /\b(define|definition|classify|classification|taxonomy|meaning|compare|difference between|glossary)\b/.test(
      text,
    )
  ) {
    return {
      label: "Semantic or classificatory wording",
      explanation:
        "This input appears to ask for meaning, classification, comparison, or conceptual organisation rather than causal modelling.",
      repair:
        "To proceed causally, convert the semantic relation into a question about whether changing or varying X would make a difference to Y.",
    };
  }

  if (
    /\b(why|explain|account for|understand|interpret|make sense of)\b/.test(
      text,
    )
  ) {
    return {
      label: "Explanatory but not yet causal-form wording",
      explanation:
        "This input seeks explanation, but it does not yet settle a Pearlian causal structure with an authorised X, Y, and direction of influence.",
      repair:
        "To proceed causally, nominate the proposed cause or intervention (X) and the outcome whose change is being considered (Y).",
    };
  }

  return {
    label: "Not yet in causal form",
    explanation:
      "This input does not yet authorise a Pearlian causal model because X, Y, and the direction of influence are not sufficiently settled.",
    repair:
      "To proceed, draft a minimally admissible X → Y causal question, then submit it through the ordinary PAT workflow.",
  };
}

export default function PatNonCausalPanel({
  input,
  draftKey,
  drafts,
  setDrafts,
  onConfirmReformulation,
}) {
  const [transferNotice, setTransferNotice] = useState("");

  const nonCausalMode = classifyNonCausalMode(input);

  const draft = normaliseNonCausalDraft(drafts?.[draftKey]);

  const recommendedReformulation = buildCausalReformulationFromDraft(draft);

  const reformulatedInput =
    draft.customReformulation.trim() || recommendedReformulation;

  const canConfirmReformulation = Boolean(
    reformulatedInput.trim() && draft.confirmed,
  );

  const buildTransferInput = () => {
    const cleanReformulation = reformulatedInput.trim();
    const cleanOriginal = String(input || "").trim();

    if (!draft.includeOriginalContext || !cleanOriginal) {
      return cleanReformulation;
    }

    return `${cleanReformulation} (${cleanOriginal})`;
  };

  const updateDraft = (patch) => {
    const shouldPreserveConfirmation =
      Object.prototype.hasOwnProperty.call(patch, "confirmed") &&
      Object.keys(patch).length === 1;

    const shouldPreservePatternVisibility =
      Object.prototype.hasOwnProperty.call(patch, "showPatternExamples") &&
      Object.keys(patch).length === 1;

    const shouldPreserveContextToggle =
      Object.prototype.hasOwnProperty.call(patch, "includeOriginalContext") &&
      Object.keys(patch).length === 1;

    setTransferNotice("");

    setDrafts((prev) => {
      const current = normaliseNonCausalDraft(prev?.[draftKey]);
      const nextBase = normaliseNonCausalDraft({
        ...current,
        ...patch,
      });

      const structuralPatch =
        Object.prototype.hasOwnProperty.call(patch, "X") ||
        Object.prototype.hasOwnProperty.call(patch, "Y") ||
        Object.prototype.hasOwnProperty.call(patch, "M") ||
        Object.prototype.hasOwnProperty.call(patch, "Z") ||
        Object.prototype.hasOwnProperty.call(patch, "pattern");

      const currentRecommended = buildCausalReformulationFromDraft(current);
      const currentCustom = current.customReformulation.trim();

      const customStillMatchesRecommendation =
        !currentCustom || currentCustom === currentRecommended;

      const nextRecommended = buildCausalReformulationFromDraft(nextBase);

      return {
        ...prev,
        [draftKey]: normaliseNonCausalDraft({
          ...nextBase,

          customReformulation:
            structuralPatch && customStillMatchesRecommendation
              ? nextRecommended
              : nextBase.customReformulation,

          confirmed:
            shouldPreserveConfirmation ||
            shouldPreservePatternVisibility ||
            shouldPreserveContextToggle
              ? nextBase.confirmed
              : false,
        }),
      };
    });
  };

  const clearDraft = () => {
    setTransferNotice("");

    setDrafts((prev) => ({
      ...prev,
      [draftKey]: normaliseNonCausalDraft({
        pattern: "direct",
        X: "",
        Y: "",
        M: "",
        Z: "",
        customReformulation: "",
        includeOriginalContext: false,
        confirmed: false,
        showPatternExamples: false,
      }),
    }));
  };

  const placeReformulationInInputPanel = () => {
    if (
      !canConfirmReformulation ||
      typeof onConfirmReformulation !== "function"
    ) {
      return;
    }

    onConfirmReformulation(buildTransferInput());
    setTransferNotice(
      "Confirmed reformulation placed in the PAT input panel. Review or edit the transferred text there, including any preserved original context, then press Submit.",
    );
  };

  return (
    <div className="mt-6 p-5 border border-amber-700 rounded-xl text-sm text-gray-300 space-y-5 bg-slate-900/40">
      <div className="font-semibold text-amber-300 text-lg">
        Your input is not yet in Pearlian causal form. Use this panel to draft a
        candidate X → Y causal reformulation, confirm that it expresses the
        question you want PAT to examine, then place it in the main PAT input
        panel below. You may review or edit the reformulated input before
        pressing Submit.
      </div>

      <div className="rounded-lg border border-slate-700/70 bg-slate-950/40 p-3 space-y-2">
        <div className="text-indigo-300 text-xs uppercase tracking-wide">
          Input received
        </div>
        <div className="text-slate-100 whitespace-pre-wrap">{input || "—"}</div>
      </div>
      <div className="rounded-lg border border-indigo-700/50 bg-indigo-950/10 p-3 space-y-2">
        <div className="text-indigo-300 text-xs uppercase tracking-wide">
          Current non-causal mode
        </div>
        <div className="text-slate-200 font-semibold">
          {nonCausalMode.label}
        </div>
        <div className="text-slate-300 leading-relaxed">
          {nonCausalMode.explanation}
        </div>
        <div className="text-slate-400 text-xs leading-relaxed">
          {nonCausalMode.repair}
        </div>
      </div>

      <div className="space-y-2">
        <div className="font-semibold text-indigo-300">
          PAT’s current reading
        </div>
        <div>
          <div>
            This input may express indication, association, classification,
            explanation, prediction, or causal interest. PAT does not treat
            those modes as DAG-ready until the analyst explicitly authorises a
            candidate causal direction.
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="font-semibold text-indigo-300">
          Why this is not yet DAG-ready
        </div>
        <div>
          PAT requires an analyst-authorised potential cause, exposure,
          condition, or intervention (X), an outcome that may change (Y), and a
          hypothesised direction of influence (X → Y). The current wording may
          be meaningful, but it does not yet settle those causal roles strongly
          enough for graph construction.
        </div>
      </div>

      <details className="rounded-lg border border-slate-700/70 bg-slate-950/30 p-4 space-y-3 group">
        <summary className="cursor-pointer text-sm font-semibold text-indigo-400 list-none">
          <div className="flex items-center justify-between gap-3">
            <span>
              Causal modelling note: moving from wording to a DAG-ready question
            </span>

            <span className="shrink-0 text-xs px-3 py-1 rounded border border-slate-600 text-slate-300 group-open:hidden">
              Show
            </span>

            <span className="shrink-0 text-xs px-3 py-1 rounded border border-slate-600 text-slate-300 hidden group-open:inline">
              Hide
            </span>
          </div>
        </summary>

        <div className="pt-3 space-y-4 text-sm text-sky-300 leading-relaxed">
          <p>
            PAT uses a Pearl-style causal-graph discipline. A Directed Acyclic
            Graph (DAG) cannot be built from a definition, description,
            judgement, classification, prediction, or association alone. Before
            causal modelling can proceed, the analyst must authorise a candidate
            causal question with a candidate cause, exposure, condition, or
            intervention <span className="font-mono text-sky-300">X</span>, an
            outcome <span className="font-mono text-sky-300">Y</span>, and a
            direction of influence from{" "}
            <span className="font-mono text-sky-300">X</span> to{" "}
            <span className="font-mono text-sky-300">Y</span>.
          </p>

          <div className="rounded-md border border-slate-700 bg-slate-950/60 px-3 py-3 space-y-2">
            <p className="font-semibold text-sky-300">
              What the analyst must settle
            </p>

            <ul className="list-disc pl-6 space-y-2 text-sky-300">
              <li>
                <span className="font-mono text-sky-300">X</span>: What factor,
                exposure, condition, choice, or intervention is being treated as
                the proposed driver?
              </li>

              <li>
                <span className="font-mono text-sky-300">Y</span>: What outcome
                could change if{" "}
                <span className="font-mono text-sky-300">X</span> were present,
                absent, increased, reduced, chosen, or otherwise varied?
              </li>

              <li>
                Direction: What is the candidate claim —{" "}
                <span className="font-mono text-sky-300">X → Y</span>? If the
                direction is not settled, the input is not yet DAG-ready.
              </li>
            </ul>
          </div>

          <p>
            Reformulating the input does not prove that{" "}
            <span className="font-mono text-sky-300">X</span> causes{" "}
            <span className="font-mono text-sky-300">Y</span>. It only places
            the question into a causal form that PAT can examine in the ordinary
            workflow.
          </p>

          <p>
            Mediators <span className="font-mono text-sky-300">M</span> and
            background variables{" "}
            <span className="font-mono text-sky-300">Z</span> are considered
            later, during role review and DAG construction. At this stage, the
            task is only to decide whether there is a candidate{" "}
            <span className="font-mono text-sky-300">X → Y</span> question worth
            modelling.
          </p>

          <p>
            Pearlian authorisation begins later, after the ordinary PAT workflow
            has reviewed variables, candidate roles, directed relations, and DAG
            assumptions. This panel blocks premature causal escalation; it does
            not license causality.
          </p>

          <p className="pt-2 border-t border-slate-700/60 text-xs text-slate-500 leading-relaxed">
            Footnote: “Pearl-style” refers here to causal-graph reasoning that
            makes variables, directed relations, and graph assumptions explicit
            before intervention reasoning. It does not imply endorsement,
            affiliation, or approval of PAT by Judea Pearl or any cited author.
            See Pearl, <span className="italic">Causality</span>, 2nd ed.
            (2009), and Pearl and Mackenzie,{" "}
            <span className="italic">The Book of Why</span> (2018).
          </p>
        </div>
      </details>

      <div className="rounded-lg border border-slate-700/70 bg-slate-950/30 p-4 space-y-4">
        <div>
          <div className="font-semibold text-indigo-300 mb-1">
            Draft a minimal X → Y causal question
          </div>
          <div className="text-slate-400 text-xs">
            Minimum requirement: nominate one potential cause, exposure,
            condition, or intervention (X) and one outcome that may change (Y).
            Optional M and Z variables may be added as drafting aids, but they
            are not final graph roles and can also be allocated later through
            the normal PAT workflow after submission.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="space-y-1">
            <span className="text-xs text-slate-400">
              Potential cause or intervention (X)
            </span>
            <input
              type="text"
              value={draft.X}
              onChange={(e) => updateDraft({ X: e.target.value })}
              placeholder="Enter X"
              className="form-element w-full"
            />
          </label>

          <label className="space-y-1">
            <span className="text-xs text-slate-400">
              Nominate 'one' outcome that may change (Y)
            </span>
            <input
              type="text"
              value={draft.Y}
              onChange={(e) => updateDraft({ Y: e.target.value })}
              placeholder="Enter Y"
              className="form-element w-full"
            />
          </label>

          <label className="space-y-1">
            <span className="text-xs text-slate-400">
              Optional mediator (M)
            </span>
            <input
              type="text"
              value={draft.M}
              onChange={(e) => updateDraft({ M: e.target.value })}
              placeholder="Optional"
              className="form-element w-full"
            />
          </label>

          <label className="space-y-1">
            <span className="text-xs text-slate-400">
              Optional confounder or adjustment variable (Z)
            </span>
            <input
              type="text"
              value={draft.Z}
              onChange={(e) => updateDraft({ Z: e.target.value })}
              placeholder="Optional"
              className="form-element w-full"
            />
          </label>
        </div>

        <div className="rounded-lg border border-slate-700/60 bg-slate-950/20 p-3 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs text-indigo-300 font-semibold">
                Interactive causal pattern examples
              </div>
              <div className="text-xs text-slate-500 mt-1">
                These examples are optional scaffolds. They are not required if
                you already know how you want to express the causal question.
              </div>
            </div>

            <button
              type="button"
              onClick={() =>
                updateDraft({ showPatternExamples: !draft.showPatternExamples })
              }
              className="text-xs px-3 py-1 rounded border border-slate-600 text-slate-300 hover:bg-slate-800 transition"
            >
              {draft.showPatternExamples ? "Hide" : "Show"}
            </button>
          </div>

          {draft.showPatternExamples && (
            <label className="block space-y-1">
              <span className="text-xs text-slate-400">
                Choose an optional PAT-suitable pattern that best represents
                your causal model
              </span>
              <select
                value={draft.pattern}
                onChange={(e) => updateDraft({ pattern: e.target.value })}
                className="form-element w-full"
              >
                <option value="direct">Does X cause Y?</option>
                <option value="increase_decrease">
                  Does X increase or decrease Y?
                </option>
                <option value="counterfactual">
                  Would Y change if X were altered?
                </option>
                <option value="mediated">
                  Is the effect of X on Y mediated by M?
                </option>
                <option value="confounded">
                  Is the relation between X and Y adjusted for Z?
                </option>
              </select>
            </label>
          )}
        </div>

        <div className="rounded-lg border border-slate-700/60 bg-slate-900/70 p-3 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-indigo-300 uppercase tracking-wide">
              Reformulated causal input
            </div>
            <div className="text-[0.7rem] text-slate-500">
              Editable analyst draft
            </div>
          </div>

          <textarea
            value={reformulatedInput || nonCausalPatternLabel(draft.pattern)}
            onChange={(e) =>
              updateDraft({
                customReformulation: e.target.value,
              })
            }
            placeholder="Write or edit the causal question you want PAT to analyse."
            className="form-element w-full min-h-[5.5rem] resize-y"
          />

          {recommendedReformulation && (
            <div className="text-[0.7rem] text-slate-500 leading-relaxed">
              Selected causal-pattern scaffold: {recommendedReformulation}
            </div>
          )}

          <label className="flex items-start gap-3 pt-2 border-t border-slate-700/50 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.includeOriginalContext}
              onChange={(e) =>
                updateDraft({ includeOriginalContext: e.target.checked })
              }
              className="mt-1"
            />
            <span className="text-xs text-slate-400 leading-relaxed">
              Include the original non-causal input in brackets after the
              reformulated causal question. This may help preserve possible
              additional outcomes, mediators, confounders, competing
              explanations, or contextual conditions without changing the
              reformulation into a separate instruction block.
            </span>
          </label>
        </div>

        <div className="rounded-lg border border-amber-700/40 bg-amber-950/10 p-3 space-y-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              checked={draft.confirmed}
              disabled={!reformulatedInput}
              onChange={() => updateDraft({ confirmed: true })}
              className="mt-1"
            />
            <span className="text-sm text-slate-300 leading-relaxed">
              I confirm the edited reformulation above expresses the candidate
              causal question I want PAT to examine. I understand that this does
              not authorise a causal claim, and that the ordinary PAT workflow
              must still assess variables, roles, directed relations, and DAG
              structure. If selected, the original non-causal input will be
              appended in brackets as contextual material.
            </span>
          </label>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-1">
          <button
            type="button"
            disabled={!canConfirmReformulation}
            onClick={placeReformulationInInputPanel}
            className={`px-5 py-2 rounded-lg font-medium transition ${
              canConfirmReformulation
                ? "bg-indigo-600 hover:bg-indigo-500 text-white"
                : "bg-slate-700 text-slate-400 cursor-not-allowed"
            }`}
          >
            Place confirmed reformulation in input panel
          </button>

          <button
            type="button"
            onClick={clearDraft}
            className="px-5 py-2 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800 transition"
          >
            Clear reformulation
          </button>
        </div>
        {transferNotice && (
          <div className="text-xs text-amber-200 bg-amber-950/20 border border-amber-700/40 rounded-lg p-3">
            {transferNotice}
          </div>
        )}
      </div>

      <div className="text-slate-400 text-xs leading-relaxed">
        This panel is a pedagogical admissibility-repair aid, not a DAG
        construction workspace. It helps the analyst convert indicator,
        associational, semantic, explanatory, or malformed-causal wording into a
        candidate causal-form submission. The main PAT workflow will still
        assess variables, outcomes, mediators, confounders, directed relations,
        and graph structure after submission.
      </div>
    </div>
  );
}
